import CryptoJS from 'crypto-js';
import { useAuthStore } from '../store/authStore';
import { getApiGateway } from '../api/endpointUtils';

let isLoggingIn = false;
let isLoggingOut = false;

const LOGIN_ERROR_MESSAGES = {
  BC_ERR_AUTHENTICATION: 'Authentication failed. Please check your credentials.',
  BC_ERR_SIGNATURE: 'Invalid security signature. Please try again.',
  BC_ERR_INVALID_DATA: 'Something went wrong. Please try again.',
  BC_ERR_INVALID_PARAMETER: 'Invalid login parameters. Please check your input.',
  BC_ERR_NEED_TO_CONFIRM: 'Please verify your email address before logging in.',
  BC_ERR_BLACK_LIST: 'Your account has been blocked for security reasons. Please contact support.',
  BC_ERR_SERVER: 'Server error. Please try again later.',
  BC_ERR_DUPLICATED: 'Account is already logged in on another device.',
  BC_ERR_GOOGLEAPPLE_ACCOUNT: 'This account uses Google or Apple sign-in. Please continue with one of those.',
} as const;

const RESET_PASSWORD_ERROR_MESSAGES = {
  BC_ERR_AUTHENTICATION: 'Authentication failed. Please try again.',
  BC_ERR_SIGNATURE: 'Security verification failed. Please try again.',
  BC_ERR_INVALID_DATA: 'Invalid data received. Please try again.',
  BC_ERR_ALREADY_REQUESTED: 'Password reset email has already been sent. Please check your inbox.',
  BC_ERR_INVALID_PARAMETER: 'Invalid request parameters. Please try again.',
  BC_ERR_SERVER: 'Internal server error. Please try again later.',
  BC_ERR_NOT_FOUND: 'Email address not found. Please check your email and try again.',
} as const;

const LOGOUT_ERROR_MESSAGES = {
  BC_ERR_AUTHENTICATION: 'BCS Token not exists',
  BC_ERR_INVALID_PARAMETER: 'Invalid parameter',
  BC_ERR_SERVER: 'Internal server error'
} as const;

interface LoginParams {
  email: string;
  password: string;
}

interface LogoutParams {
  email: string;
  userToken: string;
}

export async function logout(): Promise<void> {
  const { userToken, subscribeInfo, jwtToken } = useAuthStore.getState();

  if (isLoggingOut) {
    return;
  }
  
  if (!userToken || !subscribeInfo?.userEmail) {
    throw new Error('No active session found');
  }

  try {
    isLoggingOut = true;
    const API_GATEWAY = getApiGateway();
    
    const params = new URLSearchParams({
      email: subscribeInfo.userEmail,
      user_token: userToken,
      tokenType: 'web'
    });

    // First try with JWT token
    let response = await fetch(`${API_GATEWAY}/BCS/userLogout.php?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${jwtToken}`,
      },
    });


    const data = await response.json();

    if (data.resultcode === 'BC_ERR_OK') {
      useAuthStore.getState().clearAuth();
      return;
    } else {
      const errorMessage = LOGOUT_ERROR_MESSAGES[data.resultcode as keyof typeof LOGOUT_ERROR_MESSAGES] || data.message;
      useAuthStore.getState().setError(errorMessage);
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error('Logout error:', error);
    // Even if the API call fails, we should still clear the local auth state
    useAuthStore.getState().clearAuth();
    const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred during logout';
    useAuthStore.getState().setError(errorMessage);
    throw error;
  } finally {
    isLoggingOut = false;
  }
}

export async function login({ email, password }: LoginParams): Promise<void> {
  if (isLoggingIn) {
    return;
  }

  try {
    isLoggingIn = true;
    const API_GATEWAY = getApiGateway();
    
    const formData = new URLSearchParams({
      email,
      passwd: CryptoJS.SHA256(password).toString(),
      mobile_uuid: 'web-' + crypto.randomUUID(), // Web client unique ID
      mobile_name: navigator.userAgent,
      mobile_os_type: getOSType(),
      app_ver: '1.0.0',
      web_ver: '1.0.0',
      time_interval: getTimeZoneOffset().toString(),
      tokenType: 'web',
      loginApp: 'fleeta',
      loginType: 'blackvue',
      companyID: '',
      mobileLang: navigator.language.split('-')[0] || 'eng'
    });

    const response = await fetch(`${API_GATEWAY}/BCS/userLogin.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData,
    });

    const data = await response.json();
    
    console.log('📥 Login API response - FULL DATA:', {
      resultcode: data.resultcode,
      message: data.message,
      userToken: data.user_token || data.userToken,
      jwtToken: data.jwt_token || data.jwtToken,
      email: data.email || data.userEmail,
      fullResponse: data
    });
    
    if (data.resultcode === 'BC_ERR_OK') {
      console.log('✅ Login successful, calling setAuth with processed data:', {
        userToken: data.user_token || data.userToken || 'MISSING',
        jwtToken: data.jwt_token || data.jwtToken || 'MISSING',
        email: data.email || data.userEmail || 'MISSING'
      });
      useAuthStore.getState().setAuth(data);
    } else {
      const errorMessage = LOGIN_ERROR_MESSAGES[data.resultcode as keyof typeof LOGIN_ERROR_MESSAGES] || data.message;
      throw new Error(errorMessage);
    }

  } catch (error) {
    console.error('Login error:', error);
    useAuthStore.getState().setError(error instanceof Error ? error.message : 'An error occurred during login');
    throw error;
  } finally {
    isLoggingIn = false;
  }
}

function getOSType(): string {
  const platform = navigator.platform.toLowerCase();
  
  if (platform.includes('win')) return 'win32';
  if (platform.includes('mac')) return 'macos';
  if (platform.includes('linux')) return 'linux';
  
  return 'web';
}

function getTimeZoneOffset(): number {
  return new Date().getTimezoneOffset() * -1; // Convert to positive for east, negative for west
}

interface ResetPasswordResponse {
  resultcode: string;
  message?: string;
}

export async function resetPassword(email: string): Promise<{ success: boolean; alreadyRequested: boolean }> {
  try {
    const API_GATEWAY = getApiGateway();
    
    const params = new URLSearchParams({
      email,
      mobileLang: navigator.language.split('-')[0] || 'eng',
      loginApp: 'fleeta'
    });

    const response = await fetch(`${API_GATEWAY}/BCS/initPasswd.php?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    const data = await response.json();
    
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid server response format');
    }

    switch (data.resultcode) {
      case 'BC_ERR_OK':
        return { success: true, alreadyRequested: false };
        
      case 'BC_ERR_ALREADY_REQUESTED':
        return { success: true, alreadyRequested: true };
        
      case 'BC_ERR_NOT_FOUND':
        return { success: false, alreadyRequested: false };
        
      default:
        const errorMessage = RESET_PASSWORD_ERROR_MESSAGES[data.resultcode as keyof typeof RESET_PASSWORD_ERROR_MESSAGES] 
          || data.message 
          || 'An unexpected error occurred. Please try again.';
        throw new Error(errorMessage);
    }
  } catch (error) {
    console.error('Password reset error:', error);
    throw error;
  }
}